import { useEffect, useState } from "react";
import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import mixpanel from "mixpanel-browser";

import { AlertTypes, renderAlertTitle } from "../../utils/alertUtils";
import BaseModal, { EModalButtonType } from "../modals/BaseModal";
import {
  EOrganizationType,
  EUnitDetailsFilter,
  IAlert,
  IPropertyIdVariable,
  IUnitDetailsData,
} from "../../interfaces/interfaces";
import { GET_HOME_FOR_REGION, REMOVE_PROVIEW_HOME } from "../../api/gqlQueries";
import {
  organizationIdNameVar,
  smallHeaderBackArrow,
  smallHeaderDescription,
  smallHeaderTitle,
  userIdVar,
} from "../apollo/LocalState";
import { ReactComponent as Alert } from "../../img/Alert.svg";
import AlertBanner from "../unitDetails/AlertBanner";
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import EditTenantModalWizard from "../tenantManagement/removeTenant/EditTenantModalWizard";
import { SHOW_TENANT_STEP } from "../tenantManagement/TenantManagementSteps";
import Spinner from "../Spinner";

import CancelBrilliantMaxModal from "./CancelBrilliantMaxModal";
import EnrollBrilliantMaxModal from "./EnrollBrilliantMaxModal";
import HomeDetailsMainInfoForLargeScreen from "./HomeDetailsMainInfoForLargeScreen";
import HomeDetailsMainInfoForSmallScreen from "./HomeDetailsMainInfoForSmallScreen";
import HomeLargeBody from "./HomeLargeBody";
import HomePasscodeModal from "./HomePasscodeModal";
import HomeSmallBody from "./HomeSmallBody";
import InviteMemberModal from "./InviteMemberModal";

export interface IHomeDetailsProps {
  propertyIDForStorybook?: string;
}

const HomeDetails: React.FC<IHomeDetailsProps> = ({
  propertyIDForStorybook,
}) => {
  const userId = useReactiveVar(userIdVar);
  const params = useParams();
  let propertyId = propertyIDForStorybook || params.propertyId;
  if (!propertyId) {
    propertyId = "";
  }

  let { organizationId } = params;
  if (organizationId === undefined) {
    organizationId = "";
  }
  const organizationIdName = useReactiveVar(organizationIdNameVar);
  const [homePasscodeValue, setHomePasscodeValue] = useState("");
  const [showInviteTenantModal, setShowInviteTenantModal] = useState(false);
  const [showEditTenantModal, setShowEditTenantModal] = useState(false);
  const [showHomePasscodeModal, setShowHomePasscodeModal] = useState(false);
  const [currentUnitDetailsFilter, setUnitDetailsFilter] = useState(
    EUnitDetailsFilter.brilliants
  );
  const [showCancelBrilliantMaxModal, setShowCancelBrilliantMaxModal] =
    useState(false);
  const [showEnrollBrilliantMaxModal, setShowEnrollBrilliantMaxModal] =
    useState(false);
  const [showRemoveHomeModal, setShowRemoveHomeModal] = useState(false);
  const [removeTenantCurrentStep, setRemoveTenantCurrentStep] =
    useState(SHOW_TENANT_STEP);
  const { loading, data } = useQuery<IUnitDetailsData, IPropertyIdVariable>(
    GET_HOME_FOR_REGION,
    {
      variables: { propertyId },
    }
  );
  let unitName = "";
  let regionName = "";
  useEffect(() => {
    smallHeaderTitle(unitName);
    smallHeaderDescription(regionName);
    smallHeaderBackArrow(true);
    return () => {
      smallHeaderTitle("");
      smallHeaderDescription("");
      smallHeaderBackArrow(false);
    };
  }, [data]);
  useEffect(() => {
    if (
      organizationIdName.organizationType ===
      EOrganizationType.PRO_VIEW_ORGANIZATION
    ) {
      mixpanel.track("HomeDetails Viewed", {
        home_id: data?.home.homeId,
        home_name: data?.home.propertyName,
        organization_id: organizationIdName.organizationId,
        organization_name: organizationIdName.organizationName,
        organization_type: organizationIdName.organizationType,
        user_id: userId,
      });
    }
  }, []);
  const [removeProViewHome] = useMutation(REMOVE_PROVIEW_HOME);

  const navigate = useNavigate();
  const actionButtonFn = () => {
    removeProViewHome({
      onCompleted: () => {
        setShowRemoveHomeModal(false);
        navigate(-1);
      },
      update: (cache) => {
        cache.evict({
          id: `Home:${propertyId}`,
        });
      },
      variables: {
        propertyId,
      },
    });
  };
  if (loading) {
    return <Spinner />;
  }
  if (!data) {
    throw Error("No data was returned");
  }
  let brilliantDevices;
  const integrations = data.home.integrations.map((integration) => ({
    ...integration,
  }));
  {
    const controls = data.home.brilliantControls.map((control) => ({
      ...control,
    }));
    const plugs = data.home.brilliantPlugs.map((plug) => ({
      ...plug,
    }));
    const switches = data.home.brilliantSwitches.map((switchDevice) => ({
      ...switchDevice,
    }));
    brilliantDevices = {
      installedBrilliantControls: controls.sort((control1, control2) =>
        control1.name.localeCompare(control2.name)
      ),
      installedBrilliantPlugs: plugs.sort((plug1, plug2) =>
        plug1.name.localeCompare(plug2.name)
      ),
      installedBrilliantSwitches: switches.sort((switch1, switch2) =>
        switch1.name.localeCompare(switch2.name)
      ),
    };
  }
  integrations.sort((integration1, integration2) =>
    integration1.name.localeCompare(integration2.name)
  );
  const numBrilliantDevices =
    brilliantDevices.installedBrilliantControls.length +
    brilliantDevices.installedBrilliantSwitches.length +
    brilliantDevices.installedBrilliantPlugs.length;
  const numIntegrations = integrations.length;
  unitName = data.home.propertyName;
  regionName = data.home.parentProperties[0].name;
  const regionLink = `/${organizationIdName.organizationId}/homes/${data.home.parentProperties[0].propertyId}`;
  const crumbs = [
    {
      name: organizationIdName.organizationName,
      path: `/${organizationIdName.organizationId}/organization`,
    },
    {
      name: regionName,
      path: regionLink,
    },
    {
      name: unitName,
      path: propertyId,
    },
  ];

  const filteredAlerts = data.home.alerts.filter((alert) => {
    switch (alert.alarmType) {
      case AlertTypes.OfflineDevices:
        return true;
      default:
        return false;
    }
  });
  const alertsSortedByTitle = _.sortBy(filteredAlerts, (alert) => {
    return renderAlertTitle({
      alarmType: alert.alarmType,
      ambientTemperatureF: alert.ambientTemperatureF,
    });
  });
  const offlineDeviceNames = alertsSortedByTitle
    .filter((alert) => alert.alarmType === AlertTypes.OfflineDevices)
    .map((device) => device.deviceName);
  return (
    <>
      {alertsSortedByTitle.map((alert: IAlert) => {
        return (
          <AlertBanner
            alarmType={alert.alarmType}
            buildingName={alert.region.propertyName}
            deviceName={alert.deviceName}
            key={alert.id}
            unitName={alert.home.propertyName}
            ambientTemperatureF={alert.ambientTemperatureF}
            reconfiguringDeviceNames={[]}
            deviceType={alert.deviceType}
            offlineDeviceNames={offlineDeviceNames}
          />
        );
      })}

      <div className="center-align-as-column">
        <div className="padding-bottom-large center-align-as-column unit-details-main-container-small">
          {showEnrollBrilliantMaxModal && (
            <EnrollBrilliantMaxModal
              closeEnrollBrilliantMaxModal={() =>
                setShowEnrollBrilliantMaxModal(false)
              }
              homeName={data.home.propertyName}
              paymentMethod={data.home.subscription?.paymentMethod}
            />
          )}
          {showCancelBrilliantMaxModal && (
            <CancelBrilliantMaxModal
              closeCancelBrilliantMaxModal={() =>
                setShowCancelBrilliantMaxModal(false)
              }
              homeName={data.home.propertyName}
            />
          )}
          {showRemoveHomeModal && (
            <BaseModal
              actionButtonFn={actionButtonFn}
              actionButtonText="Remove"
              closeModalFn={() => setShowRemoveHomeModal(false)}
              headerText="Remove from ProView"
              buttonType={EModalButtonType.BlueButton}
            >
              <div className="text-center">
                <Alert title="alert" />
                <br />
                <br />
                <p>
                  By removing this home from ProView, you will revoke all
                  ProView and Organization access to view this home. If the
                  homeowner would like to re-invite your organization to the
                  home, they will need to re-send an email invitation to join
                  the home.
                </p>
                <br />
              </div>
            </BaseModal>
          )}
          {showInviteTenantModal && (
            <InviteMemberModal
              closeInviteTenantModal={() => setShowInviteTenantModal(false)}
              propertyId={propertyId}
              homeName={data.home.propertyName}
            />
          )}
          {showEditTenantModal && (
            <EditTenantModalWizard
              closeRemoveTenantModal={() => {
                setShowEditTenantModal(false);
              }}
              hasRemoteLockIntegration={false}
              currentStep={removeTenantCurrentStep}
              setCurrentStep={setRemoveTenantCurrentStep}
              propertyName={data.home.parentProperties[0].name}
              unitName={data.home.propertyName}
              propertyId={propertyId}
              homeId={data.home.homeId}
              setUnitDetailsFilter={setUnitDetailsFilter}
              hasIntegration={false}
              proView
              regionLink={regionLink}
            />
          )}
          {showHomePasscodeModal && (
            <HomePasscodeModal
              propertyId={propertyId}
              closeModal={() => {
                setShowHomePasscodeModal(false);
              }}
              homePasscodeValue={homePasscodeValue}
              setHomePasscodeValue={setHomePasscodeValue}
              existingHomePasscode={data.home.homePasscode}
            />
          )}
          <div className="unit-details-main-container">
            <Breadcrumbs
              crumbs={crumbs}
              rootCrumb={{ name: "Dashboard", path: "/" }}
            />

            <HomeDetailsMainInfoForLargeScreen
              home={data.home}
              numBrilliantDevices={numBrilliantDevices}
              numIntegrations={numIntegrations}
              openCancelBrilliantMaxModal={() =>
                setShowCancelBrilliantMaxModal(true)
              }
              openEnrollBrilliantMaxModal={() =>
                setShowEnrollBrilliantMaxModal(true)
              }
              openRemoveHomeModal={() => setShowRemoveHomeModal(true)}
              subscription={data.home.subscription}
            />
            <HomeDetailsMainInfoForSmallScreen
              currentUnitDetailsFilter={currentUnitDetailsFilter}
              numBrilliantDevices={numBrilliantDevices}
              numIntegrations={numIntegrations}
              setUnitDetailsFilter={setUnitDetailsFilter}
              openRemoveHomeModal={() => setShowRemoveHomeModal(true)}
            />
            <HomeLargeBody
              brilliantDevices={brilliantDevices}
              shouldShowChangeActivity={false}
              propertyId={propertyId}
              openEditResidentModal={() => setShowEditTenantModal(true)}
              setShowHomePasscodeModal={setShowHomePasscodeModal}
              setShowInviteTenantModal={setShowInviteTenantModal}
              integrations={integrations}
            />
            <HomeSmallBody
              brilliantDevices={brilliantDevices}
              currentUnitDetailsFilter={currentUnitDetailsFilter}
              propertyId={propertyId}
              setShowHomePasscodeModal={setShowHomePasscodeModal}
              setShowInviteTenantModal={setShowInviteTenantModal}
              openEditTenantModalWizard={() => setShowEditTenantModal(true)}
              integrations={integrations}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomeDetails;
